import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch } from '@alfa-bank/site-content-switcher/switch';
import { Case } from '@alfa-bank/site-content-switcher/case';
import cn from 'classnames';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';
import { selectAppData, initApp } from '@alfa-bank/site-dc-form-api/store';

import { deviceConfig, scrollTopOffset } from './config';
import { getNamedContainerKey, getClearProps } from './utils/helpers';
import type { TDcForm } from './models';
import { useAnalytics } from './utils/use-analytics';

export const DcForm: FC<TDcForm> = props => {
    const {
        ChangePhoneNumber,
        CheckData,
        DeliveryStep,
        Draft,
        Error,
        FirstStep,
        GentleErrorPage,
        Heading,
        Layout,
        PassportStep,
        SmsConfirmation,
        ThanksPage,
        abTest,
        additionalProducts,
        birthday,
        bonusCard,
        card,
        cashback,
        cashbackPackages,
        children,
        className,
        client,
        clientPlate,
        defaultAdditionalServices,
        draft,
        draftId,
        email,
        embossing,
        errors,
        exceptions,
        fio,
        flexibleAlfaCard = true,
        formAction,
        formHash,
        gentleError,
        headingFirstStep,
        isMobile,
        notResident,
        onChangeStep,
        passport,
        phone,
        prefilledFirstStep,
        product,
        productDesign,
        progress,
        progressType,
        refId,
        resident,
        scenarioId,
        secondStep,
        smsConfirmation,
        sopdList,
        subHeading,
        thanksPage,
        withDraftFirstStep,
    } = props;

    const layoutRef = useRef<HTMLDivElement>(null);
    const { status, step } = useSelector(selectAppData);
    const dispatch = useDispatch();
    const [isFirstRender, setIsFirstRender] = useState(true);
    const { secondStep: secondStepSlot, thanksPage: thanksPageSlot } = useNamedContainers(children);

    useEffect(() => {
        if (!isFirstRender) {
            onChangeStep?.(step);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    /** Делает подскролл к верху формы при переходе между экранами */
    useEffect(() => {
        const isScrollToDraft = !(step === 'draft' && draft?.disableScrollToDraft);

        if (layoutRef.current && step !== 'firstStep' && isScrollToDraft) {
            const offset = isMobile ? scrollTopOffset.mobile : scrollTopOffset.desktop;

            window?.scrollTo({ top: layoutRef.current.offsetTop - offset, behavior: 'smooth' });
        }
    }, [step, isMobile, draft]);

    useAnalytics(step, status);

    useEffect(() => {
        setIsFirstRender(false);
    }, []);

    const config = React.useMemo(() => getClearProps(props), [props]);

    useEffect(() => {
        dispatch(
            initApp({
                appConfig: config,
                gentleErrorEnabled: !!gentleError?.enabled,
                timeSlotDaysCount: secondStep.timeSlotDaysCount,
                prefilledFirstStep,
                draft: {
                    draftId,
                    cookieParams: draft?.cookie,
                    cardId: card.cardId,
                    isDraftEnabled: !!draft,
                },
                smsConfirmationAttempts: smsConfirmation.attempts,
                device:
                    navigator?.userAgent &&
                    getNamedContainerKey({ ...deviceConfig, ...exceptions }, navigator.userAgent),
            })
        );
    }, [
        card.cardId,
        config,
        dispatch,
        prefilledFirstStep,
        draft,
        draftId,
        exceptions,
        gentleError?.enabled,
        secondStep.timeSlotDaysCount,
        smsConfirmation.attempts,
    ]);

    return (
        <>
            <div ref={layoutRef} className={cn(className)}>
                <Layout>
                    {flexibleAlfaCard && headingFirstStep && (
                        <Heading heading={headingFirstStep} step={step} />
                    )}
                    <Switch visible={step}>
                        <Case id="firstStep">
                            <FirstStep
                                abTest={abTest}
                                additionalProducts={additionalProducts}
                                birthday={birthday}
                                bonusCard={bonusCard}
                                card={card}
                                cashback={cashback}
                                cashbackPackages={cashbackPackages}
                                client={client}
                                clientPlate={clientPlate}
                                defaultAdditionalServices={defaultAdditionalServices}
                                email={email}
                                embossing={embossing}
                                fio={fio}
                                flexibleAlfaCard={flexibleAlfaCard}
                                formAction={formAction}
                                formHash={formHash}
                                heading={flexibleAlfaCard ? undefined : headingFirstStep}
                                notResident={notResident}
                                phone={phone}
                                product={product}
                                productDesign={productDesign}
                                progress={progress}
                                progressType={progressType}
                                refId={refId}
                                resident={resident}
                                scenarioId={scenarioId}
                                sopdList={sopdList}
                                subHeading={subHeading}
                                withDraftFirstStep={withDraftFirstStep}
                            >
                                {children}
                            </FirstStep>
                        </Case>
                        <Case id="smsConfirm">
                            <SmsConfirmation
                                attempts={smsConfirmation.attempts}
                                countdownDuration={smsConfirmation.countdownDuration}
                            />
                        </Case>
                        <Case id="passport">
                            <PassportStep {...passport} />
                        </Case>
                        <Case id="changePhoneNumber">
                            <ChangePhoneNumber />
                        </Case>
                        <Case id="delivery">
                            {secondStepSlot || (
                                <DeliveryStep {...secondStep} progressType={progressType}>
                                    {children}
                                </DeliveryStep>
                            )}
                        </Case>
                        <Case id="checkData">
                            <CheckData text="Проверяем данные" />
                        </Case>
                        <Case id="thanksPage">
                            {thanksPageSlot || (
                                <ThanksPage thanksPage={thanksPage}> {children}</ThanksPage>
                            )}
                        </Case>
                        <Case id="error">
                            <Error errors={errors} />
                        </Case>
                        {gentleError?.enabled && (
                            <Case id="gentleErrorPage">
                                <GentleErrorPage {...gentleError} />
                            </Case>
                        )}
                        {draft && (
                            <Case id="draft">
                                <Draft {...draft} card={card} />
                            </Case>
                        )}
                    </Switch>
                </Layout>
            </div>
        </>
    );
};
